import { LoanType } from '../LoanConstants';

export interface LoanTermOption {
    key: string;
    name: string;
    initialTermMonths: number | null;
    contractualExtension1Months: number | null;
    contractualExtension2Months: number | null;
}

export const CUSTOM_TERM_KEY = 'custom';

export const getLoanTermOptions = (loanTypeId: LoanType): LoanTermOption[] => {
    let options: LoanTermOption[];

    switch (loanTypeId) {
        case LoanType.Construction:
            options = [
                {
                    key: '10-4',
                    name: '10 + 4 Months',
                    initialTermMonths: 10,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
                {
                    key: '12-4',
                    name: '12 + 4 Months',
                    initialTermMonths: 12,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
                {
                    key: '14-4',
                    name: '14 + 4 Months',
                    initialTermMonths: 14,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
                {
                    key: '18-0',
                    name: '18 Months',
                    initialTermMonths: 18,
                    contractualExtension1Months: null,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.Rental:
            options = [
                {
                    key: '60-24',
                    name: '5 + 2 Years',
                    initialTermMonths: 60,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
                {
                    key: '120-24',
                    name: '10 + 2 Years',
                    initialTermMonths: 120,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
                {
                    key: '180-24',
                    name: '15 + 2 Years',
                    initialTermMonths: 180,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.BuildRent:
            options = [
                {
                    key: '60-24',
                    name: '5 + 2 Years',
                    initialTermMonths: 60,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
                {
                    key: '120-24',
                    name: '10 + 2 Years',
                    initialTermMonths: 120,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
                {
                    key: '180-24',
                    name: '15 + 2 Years',
                    initialTermMonths: 180,
                    contractualExtension1Months: 24,
                    contractualExtension2Months: null,
                },
                {
                    key: '360',
                    name: '30 Year',
                    initialTermMonths: 360,
                    contractualExtension1Months: null,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.FixFlip:
            options = [
                {
                    key: '8-4',
                    name: '8 + 4 Months',
                    initialTermMonths: 8,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
                {
                    key: '10-4',
                    name: '10 + 4 Months',
                    initialTermMonths: 10,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.Land:
            options = [
                {
                    key: '10-4',
                    name: '10 + 4 Months',
                    initialTermMonths: 10,
                    contractualExtension1Months: 4,
                    contractualExtension2Months: null,
                },
                {
                    key: '12-6',
                    name: '12 + 6 Months',
                    initialTermMonths: 12,
                    contractualExtension1Months: 6,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.Model:
            options = [
                {
                    key: '12-0',
                    name: '12 Months',
                    initialTermMonths: 12,
                    contractualExtension1Months: null,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.Bridge:
            options = [
                {
                    key: '6-6',
                    name: '6 + 6 Months',
                    initialTermMonths: 6,
                    contractualExtension1Months: 6,
                    contractualExtension2Months: null,
                },
                {
                    key: '9-6',
                    name: '9 + 6 Months',
                    initialTermMonths: 9,
                    contractualExtension1Months: 6,
                    contractualExtension2Months: null,
                },
            ];
            break;
        case LoanType.MasterNote:
        case LoanType.SubNoteConstruction:
        case LoanType.SubNoteFixFlip:
            options = [
                {
                    key: '60-0',
                    name: '5 Years',
                    initialTermMonths: 60,
                    contractualExtension1Months: null,
                    contractualExtension2Months: null,
                },
            ];
            break;
        default:
            options = [];
    }

    // Add Custom option at the end for all loan types
    options.push({
        key: CUSTOM_TERM_KEY,
        name: 'Custom',
        initialTermMonths: null,
        contractualExtension1Months: null,
        contractualExtension2Months: null,
    });

    return options;
};

export const findMatchingTermOptionKey = (
    loanTypeId: LoanType,
    initialTermMonths: number | null,
    contractualExtension1Months: number | null,
    contractualExtension2Months: number | null
): string => {
    const options = getLoanTermOptions(loanTypeId);

    const match = options.find(
        (opt) =>
            opt.key !== CUSTOM_TERM_KEY &&
            opt.initialTermMonths === initialTermMonths &&
            opt.contractualExtension1Months === contractualExtension1Months &&
            opt.contractualExtension2Months === contractualExtension2Months
    );

    if (match) {
        return match.key ?? '';
    }

    // If all values are null, no term has been selected yet
    if (initialTermMonths == null && contractualExtension1Months == null && contractualExtension2Months == null) {
        return '';
    }

    // Non-null values that don't match a preset = custom term
    return CUSTOM_TERM_KEY;
};

export const formatLoanTermShort = (
    loanTypeId: LoanType | null | undefined,
    initialTermMonths: number | null | undefined,
    contractualExtension1Months: number | null | undefined,
    contractualExtension2Months: number | null | undefined
): string => {
    if (loanTypeId == null) return '';
    const key = findMatchingTermOptionKey(
        loanTypeId,
        initialTermMonths ?? null,
        contractualExtension1Months ?? null,
        contractualExtension2Months ?? null
    );
    if (!key) return '';
    if (key === CUSTOM_TERM_KEY) return 'Cust.';
    const option = getLoanTermOptions(loanTypeId).find((o) => o.key === key);
    if (!option) return '';
    return option.name.replace(/\s+(Years?|Months?)$/i, '').replace(/\s+/g, '');
};
